import { useMemo, useState } from 'react';
import type { Ticket } from '@/lib/mock';
import { useNow } from '@/lib/useNow';
import { useAuth } from '@/lib/auth';
import { useRole } from '@/lib/role';
import { 
  Inbox, AlertTriangle, Clock, CheckCircle2, 
  Activity, ShieldAlert, MessageSquare, 
  UserCircle, ArrowRight, Mail, RefreshCw
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

export function AgentDashboard({ 
  tickets, 
  onOpenTicket, 
  onRefresh,
  isLoading,
  error
}: { 
  tickets: Ticket[]; 
  onOpenTicket: (t: Ticket) => void; 
  onRefresh?: () => Promise<void>; 
  isLoading?: boolean;
  error?: string | null;
}) {
  const { session } = useAuth();
  const { teamId } = useRole();
  const navigate = useNavigate();
  const userId = session?.user?.id;
  const userName = session?.user?.name || session?.user?.displayName || 'Barry Allen';
  const now = useNow(5000);
  const [ticketTab, setTicketTab] = useState<'approvals' | 'my' | 'unassigned' | 'in-progress' | 'breaching'>('my');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<string>(() => new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }));

  const approvalQueueTickets = useMemo(() => {
    return tickets.filter(t => {
      const isExplicitApproval = 
        t.state === 'Pending Approval' || 
        t.state === 'Pending CAB' || 
        (t as any).approval_status === 'PENDING' || 
        (t as any).approval_status === 'Pending Approval' ||
        (t.approvals && t.approvals.some(a => a.status === 'pending'));

      const isUnassignedOpen = 
        (!t.assigneeId || t.assignee === 'Unassigned' || !t.assignee || t.assignee === '—') &&
        t.state !== 'Closed' && t.state !== 'Resolved';

      return isExplicitApproval || isUnassignedOpen;
    }).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  }, [tickets]);

  const getApprovalStatus = (t: Ticket) => {
    if (t.approvals && t.approvals.length > 0) {
      const pending = t.approvals.find(a => a.status === 'pending');
      if (pending) return 'Pending Approval';
      const approved = t.approvals.every(a => a.status === 'approved');
      if (approved) return 'Approved';
      return 'Pending';
    }
    if (t.state === 'Pending Approval') return 'Pending Approval';
    if (!t.assigneeId || t.assignee === 'Unassigned' || !t.assignee || t.assignee === '—') return 'Pending Approval';
    return 'Pending Approval';
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      if (onRefresh) {
        await onRefresh();
      }
      window.dispatchEvent(new CustomEvent('compass_tickets_changed'));
      setLastUpdated(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
    } catch (err) {
      console.error('Manual refresh failed:', err);
    } finally {
      setIsRefreshing(false);
    }
  };
  
  const myAssignedTickets = tickets.filter(t => 
    t.assigneeId === userId || 
    (userName && t.assignee && t.assignee.toLowerCase() === userName.toLowerCase()) ||
    (session?.user?.email && (t as any).assigneeEmail === session?.user?.email)
  );
  const openTickets = myAssignedTickets.filter(t => t.state === 'Open' || t.state === 'Assigned' || t.state === 'In Progress');
  const inProgressTickets = myAssignedTickets.filter(t => t.state === 'In Progress');
  
  const unassignedTeamTickets = tickets.filter(t => (!t.assignee || t.assignee === 'Unassigned' || t.assignee === '—' || !t.assigneeId) && t.state !== 'Closed' && t.state !== 'Resolved' && (!teamId || !t.teamId || (t as any).teamId === teamId));
  const breachingTickets = tickets.filter(t => t.slaDueAt && (now > t.slaDueAt || (t.slaDueAt - now) < 7200000) && t.state !== 'Closed' && t.state !== 'Resolved' && (!teamId || !t.teamId || (t as any).teamId === teamId));
  
  const displayedTickets = useMemo(() => {
    switch (ticketTab) {
      case 'approvals': return approvalQueueTickets;
      case 'unassigned': return unassignedTeamTickets;
      case 'in-progress': return inProgressTickets;
      case 'breaching': return breachingTickets;
      case 'my':
      default: return myAssignedTickets;
    }
  }, [ticketTab, approvalQueueTickets, myAssignedTickets, unassignedTeamTickets, inProgressTickets, breachingTickets]);
  const slaDueToday = myAssignedTickets.filter(t => t.slaDueAt && t.slaDueAt > now && (t.slaDueAt - now) < 86400000 && t.state !== 'Closed' && t.state !== 'Resolved');
  
  const emailTickets = myAssignedTickets.filter(t => t.source === 'Email' && t.state !== 'Closed' && t.state !== 'Resolved');
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  
  const completedToday = myAssignedTickets.filter(t => 
    t.state === 'Resolved' || t.state === 'Closed'
  );

  // Dynamically calculate Chart Data for Alerts by Severity (P1 vs P2 over the last 15 days)
  const chartData = useMemo(() => {
    const data = [];
    // Generate the last 15 days
    for (let i = 14; i >= 0; i--) {
      const d = new Date(now - i * 86400000);
      d.setHours(0, 0, 0, 0);
      const endOfDay = new Date(d);
      endOfDay.setHours(23, 59, 59, 999);
      
      const dayTickets = tickets.filter(t => t.createdAt >= d.getTime() && t.createdAt <= endOfDay.getTime());
      
      data.push({
        name: d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }),
        critical: dayTickets.filter(t => t.priority === 'P1').length,
        high: dayTickets.filter(t => t.priority === 'P2').length
      });
    }
    return data;
  }, [tickets, now]);

  // Mock Mentions Data
  const mentions = [
    { id: 1, author: 'Mike Oliver', text: 'I\'d like to pick your brain on this issue. How do you think we should go about this?', ticket: "My laptop's been crashing since yesterday, help!", date: '10/12/2022 12:08', isMention: true }
  ];

  // Approvals Data
  const myPendingApprovals = tickets.filter(t => t.approvals && t.approvals.some(a => a.status === 'pending' && a.approver === userName));
  const teamPendingApprovals = tickets.filter(t => t.approvals && t.approvals.some(a => a.status === 'pending') && (!teamId || (t as any).teamId === teamId));
  const pendingApprovalsToShow = myPendingApprovals.length > 0 ? myPendingApprovals : teamPendingApprovals;

  // Additional Ticket Stats
  const escalatedCount = tickets.filter(t => (t.escalationLevel || 0) > 0 && (!teamId || (t as any).teamId === teamId)).length;
  const onHoldCount = myAssignedTickets.filter(t => t.state === 'On Hold' || t.state === 'Pending Vendor').length;

  return (
    <div className="flex h-full flex-col bg-theme overflow-y-auto">
      {/* Top Banner similar to the image */}
      <div className="bg-surface border-b border-[var(--color-neo-border)] p-4 flex flex-wrap items-center justify-between gap-4 shadow-sm sticky top-0 z-10">
        <div className="flex items-center gap-4">
          <div className="h-12 w-12 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center text-white text-lg font-bold shadow-sm">
            {userName.charAt(0).toUpperCase()}
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-bold text-foreground flex items-center gap-2">
                Good day, {userName} <span className="text-xl">👋</span>
              </h1>
              <span className="inline-flex items-center gap-1 rounded-full bg-[var(--color-primary)]/10 border border-[var(--color-primary)]/20 px-2.5 py-0.5 text-xs font-semibold text-[var(--color-primary)]">
                Technician Dashboard
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">IT / Service Desk · Last Updated: {lastUpdated}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button 
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="neo-interactive bg-surface border border-border text-foreground flex items-center gap-1.5 px-3.5 py-2 rounded-[1.5rem] text-sm font-medium hover:bg-muted/30 transition shadow-sm cursor-pointer disabled:opacity-50"
            title="Refresh tickets from Backend API"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button 
            onClick={() => navigate('/technician/queue')}
            className="neo-interactive bg-[var(--color-primary)] text-[var(--color-primary-content)] flex items-center gap-2 px-4 py-2 rounded-[1.5rem] text-sm font-medium hover:brightness-110 transition shadow-sm"
          >
            <Inbox className="h-4 w-4" />
            Open Work Queue
            <span className="ml-1 rounded-full bg-black/20 dark:bg-white/20 px-1.5 py-0.5 text-xs font-bold">
              {openTickets.length}
            </span>
          </button>
        </div>
      </div>

      {error && (
        <div className="max-w-[1600px] mx-auto w-full px-6 pt-4">
          <div className="p-3.5 rounded-[1rem] bg-destructive/10 border border-destructive/20 flex items-center justify-between text-sm text-destructive">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span>Unable to load dashboard data. {error}</span>
            </div>
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="px-3 py-1 bg-destructive text-white rounded-md text-xs font-semibold hover:brightness-110 cursor-pointer flex items-center gap-1"
            >
              <RefreshCw className={`h-3 w-3 ${isRefreshing ? 'animate-spin' : ''}`} />
              Retry
            </button>
          </div>
        </div>
      )}

      {error && tickets.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center p-12 text-center min-h-[450px]">
          <div className="p-4 rounded-full bg-destructive/10 text-destructive mb-4">
            <AlertTriangle className="h-10 w-10" />
          </div>
          <h2 className="text-xl font-bold text-foreground mb-2">Unable to load dashboard data.</h2>
          <p className="text-sm text-muted-foreground max-w-md mb-6">{error}</p>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="neo-interactive bg-[var(--color-primary)] text-[var(--color-primary-content)] flex items-center gap-2 px-5 py-2.5 rounded-[1.5rem] text-sm font-semibold hover:brightness-110 transition shadow-sm cursor-pointer"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            Retry
          </button>
        </div>
      ) : (
      <div className="p-6 grid grid-cols-1 xl:grid-cols-3 gap-6 max-w-[1600px] mx-auto w-full">
        
        {/* Left Column - Main Dashboard Area */}
        <div className="xl:col-span-2 space-y-6">
          
          {/* Chart Widget */}
          <div className="neo rounded-[1.5rem] overflow-hidden flex flex-col h-[300px]">
            <div className="px-5 py-4 border-b border-border flex items-center justify-between">
              <h2 className="font-semibold flex items-center gap-2 text-foreground">
                <Activity className="h-4 w-4 text-orange-500" />
                Alerts by Severity
              </h2>
            </div>
            <div className="flex-1 p-4">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }} dy={10} angle={-35} textAnchor="end" />
                  <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }} />
                  <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid var(--border)' }} />
                  <Line type="monotone" dataKey="critical" stroke="#ef4444" strokeWidth={2} dot={{ r: 3, fill: '#ef4444' }} activeDot={{ r: 5 }} />
                  <Line type="monotone" dataKey="high" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3, fill: '#3b82f6' }} activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Metrics Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            
            {/* KPI Card 1 */}
            <button 
              onClick={() => navigate('/technician/queue?view=my')}
              className="neo rounded-[1.5rem] p-5 relative overflow-hidden flex flex-col items-center justify-center min-h-[140px] hover:border-primary/50 transition-colors cursor-pointer group"
            >
              <div className="absolute top-3 left-3 bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400 p-1.5 rounded-md group-hover:scale-110 transition-transform">
                <Inbox className="h-4 w-4" />
              </div>
              <h3 className="text-4xl font-bold text-foreground mt-4">{openTickets.length}</h3>
              <p className="text-sm font-medium text-muted-foreground mt-2">My Open Tickets</p>
            </button>

            {/* KPI Card 2 */}
            <button 
              onClick={() => navigate('/technician/queue?view=pending-approvals')}
              className="neo rounded-[1.5rem] p-5 relative overflow-hidden flex flex-col items-center justify-center min-h-[140px] hover:border-primary/50 transition-colors cursor-pointer group"
            >
              <div className="absolute top-3 left-3 bg-purple-100 dark:bg-purple-500/20 text-purple-600 dark:text-purple-400 p-1.5 rounded-md group-hover:scale-110 transition-transform">
                <ShieldAlert className="h-4 w-4" />
              </div>
              <h3 className="text-4xl font-bold text-foreground mt-4">{pendingApprovalsToShow.length}</h3>
              <p className="text-sm font-medium text-muted-foreground mt-2">Pending Approvals</p>
            </button>

            {/* KPI Card 3 */}
            <button 
              onClick={() => navigate('/technician/queue?view=unassigned')}
              className="neo rounded-[1.5rem] p-5 relative overflow-hidden flex flex-col items-center justify-center min-h-[140px] hover:border-primary/50 transition-colors cursor-pointer group"
            >
              <div className="absolute top-3 left-3 bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400 p-1.5 rounded-md group-hover:scale-110 transition-transform">
                <UserCircle className="h-4 w-4" />
              </div>
              <h3 className="text-4xl font-bold text-foreground mt-4">{unassignedTeamTickets.length}</h3>
              <p className="text-sm font-medium text-muted-foreground mt-2">Unassigned Tickets</p>
            </button>

            {/* KPI Card 4 */}
            <button 
              onClick={() => navigate('/technician/queue?view=in-progress')}
              className="neo rounded-[1.5rem] p-5 relative overflow-hidden flex flex-col items-center justify-center min-h-[140px] hover:border-primary/50 transition-colors cursor-pointer group"
            >
              <div className="absolute top-3 left-3 bg-amber-100 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400 p-1.5 rounded-md group-hover:scale-110 transition-transform">
                <Activity className="h-4 w-4" />
              </div>
              <h3 className="text-4xl font-bold text-foreground mt-4">{inProgressTickets.length}</h3>
              <p className="text-sm font-medium text-muted-foreground mt-2">In Progress</p>
            </button>

            {/* KPI Card 5 */}
            <button 
              onClick={() => navigate('/technician/queue?view=breaching')}
              className="neo rounded-[1.5rem] p-5 relative overflow-hidden flex flex-col items-center justify-center min-h-[140px] hover:border-primary/50 transition-colors cursor-pointer group"
            >
              <div className="absolute top-3 left-3 bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400 p-1.5 rounded-md group-hover:scale-110 transition-transform">
                <Clock className="h-4 w-4" />
              </div>
              <h3 className="text-4xl font-bold text-foreground mt-4">{slaDueToday.length}</h3>
              <p className="text-sm font-medium text-muted-foreground mt-2">My Tickets Due Today</p>
            </button>

            {/* KPI Card 6 */}
            <button 
              onClick={() => navigate('/technician/queue?view=on-hold')}
              className="neo rounded-[1.5rem] p-5 relative overflow-hidden flex flex-col items-center justify-center min-h-[140px] hover:border-primary/50 transition-colors cursor-pointer group"
            >
              <div className="absolute top-3 left-3 bg-orange-100 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400 p-1.5 rounded-md group-hover:scale-110 transition-transform">
                <Clock className="h-4 w-4" />
              </div>
              <h3 className="text-4xl font-bold text-foreground mt-4">{onHoldCount}</h3>
              <p className="text-sm font-medium text-muted-foreground mt-2">On Hold Tickets</p>
            </button>
            
            {/* KPI Card 7 */}
            <button 
              onClick={() => navigate('/technician/queue?view=completed')}
              className="neo rounded-[1.5rem] p-5 relative overflow-hidden flex flex-col items-center justify-center min-h-[140px] hover:border-primary/50 transition-colors cursor-pointer group"
            >
              <div className="absolute top-3 left-3 bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 p-1.5 rounded-md group-hover:scale-110 transition-transform">
                <CheckCircle2 className="h-4 w-4" />
              </div>
              <h3 className="text-4xl font-bold text-foreground mt-4">{completedToday.length}</h3>
              <p className="text-sm font-medium text-muted-foreground mt-2">Completed</p>
            </button>

            {/* KPI Card Email */}
            <button 
              onClick={() => navigate('/technician/queue?view=email')}
              className="neo rounded-[1.5rem] p-5 relative overflow-hidden flex flex-col items-center justify-center min-h-[140px] hover:border-primary/50 transition-colors cursor-pointer group"
            >
              <div className="absolute top-3 left-3 bg-cyan-100 dark:bg-cyan-500/20 text-cyan-600 dark:text-cyan-400 p-1.5 rounded-md group-hover:scale-110 transition-transform">
                <Inbox className="h-4 w-4" />
              </div>
              <h3 className="text-4xl font-bold text-foreground mt-4">{emailTickets.length}</h3>
              <p className="text-sm font-medium text-muted-foreground mt-2">Email Tickets</p>
            </button>
            
            {/* KPI Card 8 (Double span for impact) */}
            <button 
              onClick={() => navigate('/technician/queue?view=escalated')}
              className="md:col-span-2 neo rounded-[1.5rem] p-5 relative overflow-hidden flex flex-col min-h-[140px] hover:border-primary/50 transition-colors cursor-pointer group text-left"
            >
              <div className="absolute top-3 left-3 bg-destructive/10 dark:bg-destructive/20 text-destructive p-1.5 rounded-md group-hover:scale-110 transition-transform">
                <AlertTriangle className="h-4 w-4" />
              </div>
              <div className="mt-8 flex justify-between items-end h-full w-full">
                <div>
                  <h3 className="text-4xl font-bold text-foreground">{escalatedCount}</h3>
                  <p className="text-sm font-medium text-muted-foreground mt-2">Escalated Tickets</p>
                </div>
                <div className="text-sm font-medium text-primary flex items-center gap-1 group-hover:underline">
                  View Report <ArrowRight className="h-4 w-4" />
                </div>
              </div>
            </button>

          </div>
        </div>

        {/* Right Column - Feeds & Lists */}
        <div className="space-y-6">
          
          {/* Mentions Widget */}
          <div className="neo rounded-[1.5rem] overflow-hidden flex flex-col">
            <div className="px-5 py-4 border-b border-border">
              <h2 className="font-semibold text-foreground">@Mentions</h2>
            </div>
            <div className="p-4 space-y-4">
              {mentions.map(m => (
                <div key={m.id} className="text-sm">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="h-8 w-8 rounded-full bg-orange-200 text-orange-700 flex items-center justify-center font-bold text-xs shrink-0">
                      {m.author.split(' ').map(n => n[0]).join('')}
                    </div>
                    <p className="text-foreground leading-snug">
                      <span className="font-semibold">{m.author}</span> mentioned you in <span className="text-primary hover:underline cursor-pointer">{m.ticket}</span>
                    </p>
                  </div>
                  <div className="ml-10 bg-muted/30 border border-border rounded-lg p-3 relative">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                        <MessageSquare className="h-3 w-3" /> Note added by {m.author}
                      </span>
                      <span className="text-xs text-muted-foreground">{m.date}</span>
                    </div>
                    <p className="text-foreground">
                      <span className="text-blue-500 font-medium">@{userName}</span> {m.text}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Assigned Tickets List Widget */}
          <div className="neo rounded-[1.5rem] overflow-hidden flex flex-col">
            <div className="px-5 py-3 border-b border-border flex flex-wrap items-center justify-between gap-2 bg-muted/20">
              <div className="flex flex-wrap items-center gap-1.5">
                <button
                  onClick={() => setTicketTab('approvals')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                    ticketTab === 'approvals'
                      ? 'bg-[var(--color-primary)] text-[var(--color-primary-content)] shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Approval Queue ({approvalQueueTickets.length})
                </button>
                <button
                  onClick={() => setTicketTab('my')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                    ticketTab === 'my'
                      ? 'bg-[var(--color-primary)] text-[var(--color-primary-content)] shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  My Assigned ({myAssignedTickets.length})
                </button>
                <button
                  onClick={() => setTicketTab('unassigned')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                    ticketTab === 'unassigned'
                      ? 'bg-[var(--color-primary)] text-[var(--color-primary-content)] shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Unassigned ({unassignedTeamTickets.length})
                </button>
                <button
                  onClick={() => setTicketTab('in-progress')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                    ticketTab === 'in-progress'
                      ? 'bg-[var(--color-primary)] text-[var(--color-primary-content)] shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  In Progress ({inProgressTickets.length})
                </button>
                <button
                  onClick={() => setTicketTab('breaching')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                    ticketTab === 'breaching'
                      ? 'bg-[var(--color-primary)] text-[var(--color-primary-content)] shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Breaching Soon ({breachingTickets.length})
                </button>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="text-[10px] text-muted-foreground uppercase bg-muted/20">
                  {ticketTab === 'approvals' ? (
                    <tr>
                      <th className="px-4 py-3 font-semibold">Ticket Number</th>
                      <th className="px-4 py-3 font-semibold">Subject</th>
                      <th className="px-4 py-3 font-semibold">Requester</th>
                      <th className="px-4 py-3 font-semibold">Department</th>
                      <th className="px-4 py-3 font-semibold">Category</th>
                      <th className="px-4 py-3 font-semibold">Priority</th>
                      <th className="px-4 py-3 font-semibold">Status</th>
                      <th className="px-4 py-3 font-semibold">Created Date</th>
                      <th className="px-4 py-3 font-semibold">Approval Status</th>
                      <th className="px-4 py-3 font-semibold text-right">Action</th>
                    </tr>
                  ) : (
                    <tr>
                      <th className="px-4 py-3 font-semibold">Ticket ID</th>
                      <th className="px-4 py-3 font-semibold">Subject</th>
                      <th className="px-4 py-3 font-semibold">Priority</th>
                      <th className="px-4 py-3 font-semibold">Status</th>
                      <th className="px-4 py-3 font-semibold">Created</th>
                      <th className="px-4 py-3 font-semibold text-right">Assignee</th>
                    </tr>
                  )}
                </thead>
                <tbody className="divide-y divide-border">
                  {ticketTab === 'approvals' ? (
                    displayedTickets.slice(0, 10).map(t => (
                      <tr key={t.id} className="hover:bg-muted/10 transition-colors">
                        <td className="px-4 py-3 whitespace-nowrap font-mono text-xs font-semibold text-primary">
                          <button onClick={() => onOpenTicket(t)} className="hover:underline font-bold text-left">
                            {t.ref}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-foreground font-medium hover:underline truncate max-w-[200px] cursor-pointer" onClick={() => onOpenTicket(t)}>
                          {t.subject}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground whitespace-nowrap text-xs">
                          {t.requester}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground whitespace-nowrap text-xs">
                          {t.team || 'IT Support'}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground whitespace-nowrap text-xs">
                          {t.category || 'General'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${
                            (t.priority as string) === 'P1' || (t.priority as string) === 'Urgent' ? 'bg-red-500/20 text-red-500' :
                            (t.priority as string) === 'P2' || (t.priority as string) === 'High' ? 'bg-orange-500/20 text-orange-500' :
                            'bg-blue-500/20 text-blue-500'
                          }`}>
                            {t.priority}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="bg-primary/10 text-primary px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider">
                            {t.state}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-xs text-muted-foreground font-mono">
                          {t.createdAt ? new Date(t.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="inline-flex items-center gap-1 bg-amber-500/15 text-amber-600 dark:text-amber-400 px-2 py-0.5 rounded text-[10px] font-semibold">
                            {getApprovalStatus(t)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <button
                            onClick={() => onOpenTicket(t)}
                            className="px-2.5 py-1 rounded bg-[var(--color-primary)] text-[var(--color-primary-content)] text-xs font-semibold hover:brightness-110 transition shadow-sm cursor-pointer"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    displayedTickets.slice(0, 10).map(t => (
                      <tr key={t.id} className="hover:bg-muted/10 transition-colors cursor-pointer" onClick={() => onOpenTicket(t)}>
                        <td className="px-4 py-3 whitespace-nowrap font-mono text-xs font-semibold text-primary">
                          <div className="flex items-center gap-1.5">
                            <span>{t.ref}</span>
                            {t.source === 'Email' && (
                              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.2 rounded text-[10px] font-semibold bg-sky-500/10 text-sky-500 border border-sky-500/20">
                                <Mail className="h-2.5 w-2.5" /> Email
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-foreground font-medium hover:underline truncate max-w-[180px] xl:max-w-[240px]">
                          {t.subject}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${
                            (t.priority as string) === 'P1' || (t.priority as string) === 'Urgent' ? 'bg-red-500/20 text-red-500' :
                            (t.priority as string) === 'P2' || (t.priority as string) === 'High' ? 'bg-orange-500/20 text-orange-500' :
                            'bg-blue-500/20 text-blue-500'
                          }`}>
                            {t.priority}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="bg-primary/10 text-primary px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider">
                            {t.state}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-xs text-muted-foreground font-mono" title={t.createdAt ? new Date(t.createdAt).toLocaleString() : undefined}>
                          {t.createdAt ? new Date(t.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground text-right whitespace-nowrap text-xs">
                          {t.assignee?.split(' ')[0] || (
                            <span className="text-amber-500 font-medium">Unassigned</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                  {displayedTickets.length === 0 && (
                    <tr>
                      <td colSpan={ticketTab === 'approvals' ? 10 : 6} className="px-4 py-8 text-center text-muted-foreground text-xs font-medium">
                        {isLoading ? (
                          <div className="flex items-center justify-center gap-2 py-4">
                            <RefreshCw className="h-4 w-4 animate-spin text-[var(--color-primary)]" />
                            <span>Loading tickets...</span>
                          </div>
                        ) : error ? (
                          <div className="flex flex-col items-center justify-center gap-2 py-4 text-destructive">
                            <span>Unable to load tickets: {error}</span>
                            <button
                              onClick={handleRefresh}
                              disabled={isRefreshing}
                              className="px-3 py-1 bg-destructive text-white rounded-md text-xs font-semibold hover:brightness-110 cursor-pointer flex items-center gap-1"
                            >
                              <RefreshCw className={`h-3 w-3 ${isRefreshing ? 'animate-spin' : ''}`} />
                              Retry
                            </button>
                          </div>
                        ) : ticketTab === 'approvals' 
                          ? "No tickets are currently waiting for technician approval." 
                          : "No tickets found for this view."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="px-5 py-2.5 border-t border-border bg-muted/10 flex items-center justify-between">
              <span className="text-xs text-muted-foreground font-medium">
                Showing {Math.min(10, displayedTickets.length)} of {displayedTickets.length} tickets
              </span>
              <button 
                onClick={() => navigate(`/technician/queue?view=${ticketTab === 'approvals' ? 'pending-approvals' : ticketTab === 'breaching' ? 'breaching' : ticketTab === 'in-progress' ? 'in-progress' : ticketTab === 'unassigned' ? 'unassigned' : 'my'}`)}
                className="text-xs font-semibold text-[var(--color-primary)] hover:underline flex items-center gap-1 cursor-pointer"
              >
                View all in Work Queue <ArrowRight className="h-3 w-3" />
              </button>
            </div>
          </div>

          {/* Pending Approvals Widget */}
          <div className="neo rounded-[1.5rem] overflow-hidden flex flex-col">
            <div className="px-5 py-4 border-b border-border flex items-center justify-between">
              <h2 className="font-semibold text-foreground flex items-center gap-2">
                <div className="bg-purple-100 text-purple-600 dark:bg-purple-500/20 dark:text-purple-400 p-1 rounded">
                  <ShieldAlert className="h-3.5 w-3.5" />
                </div>
                Pending Approvals
                <span className="bg-muted px-2 py-0.5 rounded-full text-xs font-medium text-muted-foreground ml-2">{pendingApprovalsToShow.length}</span>
              </h2>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="text-[10px] text-muted-foreground uppercase bg-muted/20">
                  <tr>
                    <th className="px-4 py-3 font-semibold">ID</th>
                    <th className="px-4 py-3 font-semibold">Subject</th>
                    <th className="px-4 py-3 font-semibold text-right">Requester</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {pendingApprovalsToShow.slice(0, 5).map(t => (
                    <tr key={t.id} className="hover:bg-muted/10 transition-colors cursor-pointer" onClick={() => onOpenTicket(t)}>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="inline-flex items-center gap-1 bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-400 px-1.5 py-0.5 rounded text-xs font-medium">
                          {t.ref}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-primary font-medium hover:underline truncate max-w-[150px] xl:max-w-[200px]">
                        {t.subject}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground text-right whitespace-nowrap">
                        {t.requester.split(' ')[0]}
                      </td>
                    </tr>
                  ))}
                  {pendingApprovalsToShow.length === 0 && (
                    <tr>
                      <td colSpan={3} className="px-4 py-8 text-center text-muted-foreground text-xs font-medium">
                        No tickets are currently waiting for technician approval.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>
      )}
    </div>
  );
}
